// custom-size.ts — pricing of the CUSTOM-SIZE extra (PHO family).
//
// THE PRINCIPLE: start from a catalogue reference, enter the wanted format, and charge ONLY THE AREA
// DIFFERENCE at a €/m² rate specific to custom size. The extra is not a product but a top-up
// line attached to a parent, and it never exists alone.
//
// THE FOUR NON-OBVIOUS RULES
//
// 1. AREA IS NEVER DOUBLED FOR DOUBLE-SIDED. The delta uses the NOMINAL dimensions of one face; the
//    RATE carries the second face. Doubling both would bill the second face twice.
//
// 2. THE TIER IS INHERITED, NEVER RECOMPUTED: the extra reuses its parent effective rate
//    `p = 1 − retained_tier_PV / pv_tier1`. A parent at a forced price exposes no readable tier, so
//    `p = 0` and the full rate applies.
//
// 3. THE 1 m² MINIMUM APPLIES PER PIECE AND SWITCHES THE WHOLE LINE BACK TO TIER 1. Letting the
//    purchase price stay eroded by the tier while the sale price is pinned to the full rate would
//    show a margin the sale never produced. When the floor bites, both come back to tier 1 and the
//    margin is exactly 60 %.
//
// 4. THE AGENCY DISCOUNT NEVER TOUCHES THE PURCHASE PRICE. The sale price compounds tier × agency
//    (capped at 60 %); the purchase price follows the tier only, at the 2/3 slope of §9.

import { round2 } from "../core/core";
import { PA_SLOPE } from "../../data/calibration";
import { engineSettings } from "../../data";
import type { CustomSizeRate } from "../../models/data.model";

/**
 * Minimum billed per piece, in m² (setting `customSizeMinBilledM2`). Custom size is never billed below this.
 * @returns The minimum surface.
 */
export function miniCustomSizeM2(): number {
  return engineSettings().parameters.customSizeMinBilledM2;
}

/**
 * Composed discount cap, identical to the rest of the tool (§4): the setting `discountCapRate`.
 * @returns The cap as a rate (0..1).
 */
function discountCap(): number {
  return engineSettings().parameters.discountCapRate;
}

/**
 * Rounds to 3 decimals — the area precision used throughout the tool.
 * @param n - Value to round.
 * @returns The value rounded to 3 decimals.
 */
const round3 = (n: number) => Math.round(n * 1000) / 1000;

export interface DimensionsMm {
  L: number;
  H: number;
}

export interface CustomSizeEntry {
  /** NOMINAL dimensions of the base reference (one face). */
  base: DimensionsMm;
  /** Final requested dimensions (one face). */
  final: DimensionsMm;
  /** Rate derived from the parent by (family, component, faces) — never picked by the sales rep. */
  rate: CustomSizeRate;
  /** Quantity of the parent line. The extra follows it and has no quantity of its own. */
  parentQuantity: number;
  /** Effective rate INHERITED from the parent (0..1). 0 when the parent is at a forced price. */
  inheritedTierRate: number;
  /** Agency/client discount (0..1), composed on top of the tier. Never affects the purchase price. */
  agencyDiscount?: number;
}

export interface CustomSizeResult {
  /** Area difference actually requested, per piece (may be < 1 m²). */
  actualDeltaM2: number;
  /** Area actually billed, per piece: `max(actual, 1.000)`. */
  billedDeltaM2: number;
  /** Did the 1 m² floor increase the billed area? */
  minSurfaceApplied: boolean;
  /** Did the EUROS floor bite, bringing the line back to tier 1? */
  appliedFloorPrice: boolean;
  /** Line quantity = billed area × parent quantity. */
  quantity: number;
  /** Composed discount actually applied to the sale price (tier ⊕ agency, capped). */
  appliedDiscount: number;
  /** Sale price €/piece. */
  pvUnit: number;
  /** Purchase price €/piece. */
  paUnit: number;
  pvTotal: number;
  paTotal: number;
  /** Margin as a share of the sale price (0..1). Exactly 0.60 when the floor bites. */
  marginRatio: number;
}

/**
 * Extra area requested, per piece, on NOMINAL dimensions.
 * May be negative or zero — refusing is validation's job (§8), not the calculation's job to hide.
 * @param base - Nominal dimensions of the base reference.
 * @param final - Requested final dimensions.
 * @returns The area difference per piece, in m².
 */
export function deltaM2(base: DimensionsMm, final: DimensionsMm): number {
  return round3((final.L * final.H - base.L * base.H) / 1e6);
}

/**
 * Effective rate inherited from a parent line: `1 − retained tier sale price / tier 1 sale price`.
 * A parent at a FORCED price exposes no readable tier → 0, full rate (rule 2 in the header).
 * @param retainedTierPv - Sale price of the tier retained by the parent.
 * @param pvTier1 - Tier 1 sale price of the parent.
 * @param parentForced - True when the parent is at a forced price.
 * @returns The inherited rate (0..1).
 */
export function inheritedRate(retainedTierPv: number, pvTier1: number, parentForced = false): number {
  if (parentForced || !(pvTier1 > 0)) return 0;
  const inheritedTierRate = 1 - retainedTierPv / pvTier1;
  return inheritedTierRate > 0 ? inheritedTierRate : 0;
}

/**
 * Pricing of an extra that is not one: all zeroes, no badge and no floor.
 * @param actualDeltaM2 - Actual area difference (zero or negative).
 * @returns An all-zero result.
 */
function noExtra(actualDeltaM2: number): CustomSizeResult {
  return {
    actualDeltaM2: actualDeltaM2, billedDeltaM2: 0, minSurfaceApplied: false, appliedFloorPrice: false,
    quantity: 0, appliedDiscount: 0, pvUnit: 0, paUnit: 0, pvTotal: 0, paTotal: 0, marginRatio: 0,
  };
}

/**
 * Full pricing of the extra. Pure: no database read, no side effect.
 * @param entry - Formats, rate, parent quantity and inherited rate.
 * @returns The priced extra.
 */
export function priceCustomSize(entry: CustomSizeEntry): CustomSizeResult {
  const actualDeltaM2 = deltaM2(entry.base, entry.final);

  // ZERO OR NEGATIVE DELTA ⇒ NOTHING TO BILL. The 1 m² minimum is a BILLING floor for an extra
  // that exists, not a way to invent one when the requested format equals the base reference.
  // Without this guard, `max(0, 1)` gave 1 m² and a line that validation explicitly REFUSES still
  // added its full amount to the quote.
  if (!(actualDeltaM2 > 0)) return noExtra(actualDeltaM2);

  const billedDeltaM2 = Math.max(actualDeltaM2, miniCustomSizeM2());
  const quantity = round3(billedDeltaM2 * entry.parentQuantity);

  // Sale price: inherited tier COMPOSED with the agency discount, capped — never added up.
  const inheritedTierRate = Math.max(0, entry.inheritedTierRate);
  const agency = Math.max(0, entry.agencyDiscount ?? 0);
  const appliedDiscount = Math.min(discountCap(), 1 - (1 - inheritedTierRate) * (1 - agency));

  const pvComputed = round2(billedDeltaM2 * entry.rate.pv_m2 * (1 - appliedDiscount));
  // EUROS FLOOR: one square metre at the FULL RATE, whatever the tier and the agency discount did.
  const floorAmount = round2(miniCustomSizeM2() * entry.rate.pv_m2);

  let pvUnit: number;
  let paUnit: number;
  const appliedFloorPrice = pvComputed < floorAmount;
  if (appliedFloorPrice) {
    // FULL SWITCH BACK TO TIER 1 (rule 3). The purchase price returns WITH the sale price: leaving
    // it eroded by the tier would show a margin the sale never produced.
    pvUnit = floorAmount;
    paUnit = round2(miniCustomSizeM2() * entry.rate.pa_m2);
  } else {
    pvUnit = pvComputed;
    // The purchase price follows ONLY the tier, at the 2/3 slope — the agency discount cannot touch it (rule 4).
    paUnit = round2(billedDeltaM2 * entry.rate.pa_m2 * (1 - inheritedTierRate * PA_SLOPE));
  }

  const pvTotal = round2(pvUnit * entry.parentQuantity);
  const paTotal = round2(paUnit * entry.parentQuantity);
  return {
    actualDeltaM2: actualDeltaM2,
    billedDeltaM2: billedDeltaM2,
    minSurfaceApplied: billedDeltaM2 > actualDeltaM2,
    appliedFloorPrice,
    quantity: quantity,
    appliedDiscount,
    pvUnit: pvUnit,
    paUnit: paUnit,
    pvTotal,
    paTotal,
    marginRatio: pvUnit > 0 ? (pvUnit - paUnit) / pvUnit : 0,
  };
}

/**
 * Area above which the euros floor STOPS biting, for a given composed rate. Used by the book and by
 * sales explanations. Independent of the rate — it is a ratio, not a price.
 * @param inheritedTierRate - Tier rate inherited from the parent (0..1).
 * @param agencyDiscount - Agency discount (0..1).
 * @returns The area in m² above which the floor stops biting (`Infinity` when the discount is total).
 */
export function floorBiteThreshold(inheritedTierRate: number, agencyDiscount = 0): number {
  const discount = Math.min(discountCap(), 1 - (1 - Math.max(0, inheritedTierRate)) * (1 - Math.max(0, agencyDiscount)));
  return discount >= 1 ? Infinity : round3(miniCustomSizeM2() / (1 - discount));
}
